import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { SeverityLevel } from "@/types/security";

interface IssueSeverityData {
  critical: number;
  high: number;
  medium: number;
  low: number;
  criticalTrend: number;
  highTrend: number;
  mediumTrend: number;
  lowTrend: number;
}

interface IssueSeverityCardProps {
  data: IssueSeverityData;
  onSeverityClick: (severity: SeverityLevel) => void;
}

const chartConfig = {
  count: { label: "Issues", color: "hsl(var(--chart-critical))" },
} satisfies ChartConfig;

export function IssueSeverityCard({ data, onSeverityClick }: IssueSeverityCardProps) {
  const rawChartData = [
    { label: "Critical", count: data.critical, trend: data.criticalTrend, severity: "CRITICAL" as SeverityLevel, fill: "hsl(var(--chart-critical))" },
    { label: "High", count: data.high, trend: data.highTrend, severity: "HIGH" as SeverityLevel, fill: "hsl(var(--chart-high))" },
    { label: "Medium", count: data.medium, trend: data.mediumTrend, severity: "MEDIUM" as SeverityLevel, fill: "hsl(var(--chart-medium))" },
    { label: "Low", count: data.low, trend: data.lowTrend, severity: "LOW" as SeverityLevel, fill: "#00B0E4" },
  ];
  const chartData = rawChartData;

  return (
    <Card className="flex h-full flex-col rounded-[15px] shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Issue Severity</CardTitle>
        <p className="text-xs text-muted-foreground">Current versus a month ago</p>
      </CardHeader>
      <CardContent className="flex flex-1 pb-4">
        <ChartContainer config={chartConfig} className="min-h-[290px] w-full flex-1 aspect-auto">
          <BarChart accessibilityLayer data={chartData} layout="vertical" margin={{ left: 0, right: 46, top: 4, bottom: 0 }}>
            <YAxis dataKey="label" type="category" tickLine={false} axisLine={false} hide />
            <XAxis dataKey="count" type="number" hide domain={[0, "dataMax + 3"]} />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
            <Bar
              dataKey="count"
              radius={5}
              className="cursor-pointer"
              onClick={(entry) => onSeverityClick((entry as unknown as { severity: SeverityLevel }).severity)}
            >
              {chartData.map((entry) => <Cell key={entry.severity} fill={entry.fill} />)}
              <LabelList dataKey="label" position="insideLeft" offset={10} fill="white" fontSize={12} fontWeight={600} />
              <LabelList dataKey="count" position="insideRight" offset={10} fill="white" fontSize={26} fontWeight={600} />
              <LabelList
                dataKey="trend"
                position="right"
                offset={8}
                content={(props) => {
                  const index = Number(props.index ?? 0);
                  const item = chartData[index];
                  const x = Number(props.x ?? 0) + Number(props.width ?? 0) + 8;
                  const y = Number(props.y ?? 0) + Number(props.height ?? 0) / 2;
                  return (
                    <text x={x} y={y} dominantBaseline="middle" fill={item.trend > 0 ? "#dc2626" : item.trend < 0 ? "#16a34a" : "hsl(var(--muted-foreground))"} fontSize={11} fontWeight={400}>
                      {item.trend > 0 ? "+" : ""}{item.trend}%
                    </text>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
